import React, { createContext, useContext, useEffect, useState } from 'react';
import { WhiteLabelConfig } from './types';

const defaultWhiteLabel: WhiteLabelConfig = {
  logoUrl: '/assets/logo/esimora-logo.png',
  faviconUrl: '/assets/favicons/favicon.ico',
  brandName: 'Esimora',
  primaryColor: '#FD5B2C',
  secondaryColor: '#1E90FF',
};

const WhiteLabelContext = createContext<WhiteLabelConfig | null>(null);

export const useWhiteLabel = (): WhiteLabelConfig => {
  const ctx = useContext(WhiteLabelContext);
  return ctx || defaultWhiteLabel;
};

interface WhiteLabelProviderProps {
  children: React.ReactNode;
  configPath?: string;
}

export const WhiteLabelProvider = ({
  children,
  configPath = '/whiteLabel/default.json',
}: WhiteLabelProviderProps) => {
  const [whiteLabel, setWhiteLabel] = useState<WhiteLabelConfig | null>(null);

  useEffect(() => {
    fetch(configPath)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error(
              'White label config file not found (404). Make sure /whiteLabel/default.json exists in your public folder.'
            );
          }
          throw new Error(
            `Failed to fetch white label config. Status: ${res.status} ${res.statusText}`
          );
        }
        return res.json();
      })
      .then((data) => {
        const missingFields: string[] = [];
        if (typeof data.logoUrl !== 'string') missingFields.push('logoUrl');
        if (typeof data.faviconUrl !== 'string')
          missingFields.push('faviconUrl');
        if (typeof data.brandName !== 'string') missingFields.push('brandName');
        if (typeof data.primaryColor !== 'string')
          missingFields.push('primaryColor');
        if (typeof data.secondaryColor !== 'string')
          missingFields.push('secondaryColor');

        if (missingFields.length === 0) {
          setWhiteLabel(data);
        } else {
          throw new Error(
            `Invalid WhiteLabelConfig structure. Missing or invalid fields: ${missingFields.join(', ')}`
          );
        }
      })
      .catch((err) => {
        console.error(
          'Failed to load white label config:',
          err?.message || err
        );
        setWhiteLabel(defaultWhiteLabel);
      });
  }, [configPath]);

  return (
    <WhiteLabelContext.Provider value={whiteLabel || defaultWhiteLabel}>
      {children}
    </WhiteLabelContext.Provider>
  );
};
